import type {
  DropdownMenuProps as RadixDropdownMenuProps,
  DropdownMenuContentProps as RadixDropdownMenuContentProps,
} from "@radix-ui/react-dropdown-menu";
import { ReactHTMLElement, ReactNode } from "react";
import { IconProps } from "../Icon";

export const DropdownMenuOptionVariants = {
  DEFAULT: "default",
  DANGER: "danger",
  WARNING: "warning",
  HIGHLIGHT: "highlight",
  INFO: "info",
} as const;

export type DropdownMenuOptionVariantsType =
  (typeof DropdownMenuOptionVariants)[keyof typeof DropdownMenuOptionVariants];

/** describes a single item in the dropdown menu. can be infinitely nested. */
export type DropdownMenuOption =
  | {
      /** the display label for the group */
      label: string;
      /** if the item is actually just encapsulating a group of options */
      isGroup: true;
      /** group items can't be separators */
      isSeparator?: never;
      /** options to render in the group. if at-least one provided, then the
       * group will be rendered */
      options: DropdownMenuOption[];
    }
  | {
      /** the display label for the option */
      label: string;
      /** an option that shows nested options can't be a group */
      isGroup?: never;
      /** an option that shows nested options can't be a separator */
      isSeparator?: never;
      /** is the option disabled? */
      isDisabled?: boolean;
      /** is something related to the option processing? */
      isBusy?: boolean;
      /** is the option loading? */
      isLoading?: boolean;
      /** if at-least one option is provided, a sub-menu will be rendered with
       * the options and the label will be used as the "trigger" */
      options: DropdownMenuOption[];
      /** custom element to show to the left of the option. icon props are
       * provided to the given fn if you want to render an icon - you just have
       * to specify the variant */
      slotLeft?: ({
        iconProps,
      }: {
        iconProps: Partial<IconProps>;
      }) => ReactNode;
    }
  | ({
      /** the display label for the option */
      label: string;
      /** if the item should appear as an option by itself */
      isGroup?: never;
      /** an option with label can't be separators */
      isSeparator?: never;
      /** since this is a single option, nesting isn't supported */
      options?: never;
      /** custom element to show to the left of the option. icon props are
       * provided to the given fn if you want to render an icon - you just have
       * to specify the variant */
      slotLeft?: ({
        iconProps,
      }: {
        iconProps: Partial<IconProps>;
      }) => ReactNode;
      /** custom element to show to the right of the option. icon props are
       * provided to the given fn if you want to render an icon - you just have
       * to specify the variant*/
      slotRight?: ({
        iconProps,
      }: {
        iconProps: Partial<IconProps>;
      }) => ReactNode;
      /** some support text to display to the far right of an option. text will appear when/where slotRight appears. */
      flair?: string;
      /** controls the color variation of the option */
      variant?: DropdownMenuOptionVariantsType;
      /** is the option active in some way? */
      isActive?: boolean;
      /** is the option disabled? */
      isDisabled?: boolean;
      /** does the option have a tooltip? */
      tooltip?: ReactNode;
      /** is something related to the option processing? */
      isBusy?: boolean;
      /** is the option loading? */
      isLoading?: boolean;
      /** if the option is for display purposes and the label text should be
       * selectable */
      isPresentational?: boolean;
    } & Pick<ReactHTMLElement<HTMLDivElement>["props"], "onClick">)
  | {
      /** shows a non-interactive separator line at the exact spot it appears in
       * the provided options */
      isSeparator: true;
      /** separators can't have labels */
      label?: never;
      /** separators can't be groups */
      isGroup?: never;
      /** separators don't have options */
      options?: never;
    };

export type DropdownMenuProps = {
  /** the menu options configuration */
  options: DropdownMenuOption[];
  rootProps?: RadixDropdownMenuProps;
  contentProps?: RadixDropdownMenuContentProps;
};

export type DropdownMenuItemProps = {
  /** the option to render */
  option: DropdownMenuOption;
};
